import * as React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SlideCanvas, { SLIDE_WIDTH, SLIDE_HEIGHT } from './SlideCanvas';

const THUMB_WIDTH = 220;
const THUMB_HEIGHT = Math.round(THUMB_WIDTH * (SLIDE_HEIGHT / SLIDE_WIDTH));
const THUMB_SCALE = THUMB_WIDTH / SLIDE_WIDTH;

const DYNAMIC_TYPES = ['table', 'chart', 'text', 'textbox'];

function isSlideConfigured(slide) {
  const dynamicEls = (slide?.elements ?? []).filter((el) => DYNAMIC_TYPES.includes(el.type));
  if (dynamicEls.length === 0) return true;
  const slideConfigured = Boolean(slide.data_source && (slide.intent || slide.query));
  return dynamicEls.every(
    (el) => (el.data_source && (el.intent || el.query)) || slideConfigured
  );
}

/**
 * Single slide thumbnail for the left sidebar.
 * Renders the full-size SlideCanvas and uses CSS transform to scale it down,
 * so the thumbnail is a pixel-perfect miniature of the main canvas
 * (including DOM-based chart overlays).
 */
export default function SlidePreviewThumb({ slide, index, isActive, onClick, decimalPlaces = 2 }) {
  const elements = slide?.elements ?? [];
  const slideNumber = slide?.slide_number ?? index + 1;
  const configured = isSlideConfigured(slide);

  return (
    <Box
      onClick={() => onClick?.(index)}
      sx={{
        cursor: 'pointer',
        marginBottom: 1.5,
        border: isActive
          ? '2px solid #3b82f6'
          : !configured
          ? '1px solid #f59e0b'
          : '1px solid #e2e8f0',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: isActive
          ? '0 0 0 3px rgba(59,130,246,0.15), 0 2px 8px rgba(0,0,0,0.1)'
          : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: isActive ? '#3b82f6' : '#94a3b8',
          boxShadow: isActive
            ? '0 0 0 3px rgba(59,130,246,0.15), 0 4px 12px rgba(0,0,0,0.12)'
            : '0 4px 12px rgba(0,0,0,0.1)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: THUMB_WIDTH, height: THUMB_HEIGHT }}>
        {/* Render full-size canvas, then CSS-scale it down for a true miniature */}
        <Box
          sx={{
            transformOrigin: 'top left',
            transform: `scale(${THUMB_SCALE})`,
            width: SLIDE_WIDTH,
            height: SLIDE_HEIGHT,
            pointerEvents: 'none',
          }}
        >
          <SlideCanvas
            key={`thumb-${index}`}
            elements={elements}
            width={SLIDE_WIDTH}
            height={SLIDE_HEIGHT}
            isPreview
            decimalPlaces={decimalPlaces}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            left: 8,
            top: 8,
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            backgroundColor: 'rgba(30,58,95,0.75)',
            backdropFilter: 'blur(4px)',
            minWidth: 22,
            height: 22,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            px: 0.75,
            lineHeight: 1,
          }}
        >
          {slideNumber}
        </Typography>

        {!configured && (
          <Tooltip title="Data source / query not configured" arrow placement="right">
            <Box
              sx={{
                position: 'absolute',
                right: 6,
                top: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 13, color: '#d97706' }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
